import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { siteImages } from "@/lib/siteData";
import { MapPin, Phone, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getAttribution } from "@/lib/attribution";
import { HoneypotField } from "@/components/HoneypotField";
import { getRecaptchaToken } from "@/lib/recaptcha";

const serviceOptions = [
  "Headshots",
  "Studio Rental",
  "Commercial/Brand Photography",
  "360/Mirror Booth",
  "General Photography",
  "Video Services",
  "Free Image Consultation",
];

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const preService = searchParams.get("service") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: preService || "", message: "" });
  const [honeypot, setHoneypot] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const recaptcha_token = await getRecaptchaToken("contact");
    const { data, error } = await supabase.functions.invoke("contact-submit", {
      body: {
        full_name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
        attribution: getAttribution(),
        honeypot,
        recaptcha_token,
      },
    });
    setLoading(false);
    if (error || (data as any)?.error) {
      toast.error((data as any)?.error || "Could not send. Please try again.");
      return;
    }
    setSubmitted(true);
  };

  return (
    <div className="pt-20">
      <Helmet>
        <title>Contact MyStudio | Columbus GA Photo & Video Studio</title>
        <meta name="description" content="Get in touch with MyStudio Columbus, GA. Book studio time, request services, or ask about photography and video production." />
        <link rel="canonical" href="https://bookmystudiospace.com/contact" />
        <meta property="og:title" content="Contact MyStudio | Columbus GA Photo & Video Studio" />
        <meta property="og:description" content="Get in touch with MyStudio Columbus, GA — studio bookings, services, and inquiries." />
        <meta property="og:url" content="https://bookmystudiospace.com/contact" />
      </Helmet>
      {/* Hero */}
      <section className="relative h-[30vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${siteImages.heroSlider1})` }} />
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative z-10 text-center">
          <h1 className="text-3xl md:text-5xl font-display font-extrabold uppercase tracking-widest">Contact MyStudio</h1>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-foreground text-background">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h2 className="text-2xl md:text-3xl font-display font-extrabold uppercase tracking-widest mb-2">LEAVE US YOUR INFO</h2>
          <p className="text-background/60 font-body uppercase tracking-widest text-sm mb-10">Talk To You Soon!</p>

          {submitted ? (
            <div className="p-12 text-center">
              <h2 className="text-2xl font-display font-extrabold uppercase tracking-widest text-primary mb-4">MESSAGE SENT!</h2>
              <p className="text-background/60 font-body">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              <HoneypotField value={honeypot} onChange={setHoneypot} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border-b border-background/30 text-background placeholder:text-background/40 focus:outline-none focus:border-primary font-body"
                  placeholder="NAME*"
                />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border-b border-background/30 text-background placeholder:text-background/40 focus:outline-none focus:border-primary font-body"
                  placeholder="PHONE*"
                />
              </div>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border-b border-background/30 text-background placeholder:text-background/40 focus:outline-none focus:border-primary font-body"
                placeholder="EMAIL*"
              />
              <select
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border-b border-background/30 text-background focus:outline-none focus:border-primary font-body appearance-none"
              >
                <option value="" className="text-background bg-card">What Service Are You Inquiring About?</option>
                {serviceOptions.map((s) => (
                  <option key={s} value={s} className="text-background bg-card">{s}</option>
                ))}
              </select>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border-b border-background/30 text-background placeholder:text-background/40 focus:outline-none focus:border-primary font-body resize-none"
                placeholder="HOW MAY WE HELP YOU?*"
              />
              <Button type="submit" disabled={loading} className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest px-10 py-3">
                {loading ? "SENDING…" : "SUBMIT NOW"}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="space-y-3">
              <Phone className="w-6 h-6 text-primary mx-auto" />
              <h3 className="font-display font-bold uppercase tracking-widest">Phone</h3>
              <a href="tel:7064787640" className="text-muted-foreground font-body hover:text-primary transition-colors">706.478.7640</a>
            </div>
            <div className="space-y-3">
              <Mail className="w-6 h-6 text-primary mx-auto" />
              <h3 className="font-display font-bold uppercase tracking-widest">Email</h3>
              <a href="mailto:info@bookmystudiospace.com" className="text-muted-foreground font-body hover:text-primary transition-colors">info@bookmystudiospace.com</a>
            </div>
            <div className="space-y-3">
              <MapPin className="w-6 h-6 text-primary mx-auto" />
              <h3 className="font-display font-bold uppercase tracking-widest">Location</h3>
              <p className="text-muted-foreground font-body">2300 Whittlesey Rd Ste E<br />Columbus, GA 31909</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-[400px]">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3370.5!2d-84.98!3d32.46!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDI3JzM2LjAiTiA4NMKwNTgnNDguMCJX!5e0!3m2!1sen!2sus!4v1"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="MyStudio Location"
        />
      </section>
    </div>
  );
};

export default ContactPage;
